import { ApolloQueryResult, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { XCircleIcon } from '@heroicons/react/24/outline'
import { yupResolver } from '@/gf/lib/yupResolver'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import useToggle from '../hooks/useToggle'
import Money from '../modules/Money'
import {
  CREATE_SHIPPING_GROUP,
  EDIT_SHIPPING_GROUP,
  REMOVE_SHIPPING_GROUP,
} from '../mutations/shipping'
import { FETCH_SHIPPING_GROUPS } from '../queries/shipping'
import Button from './ButtonOld'
import Card from './Card'
import Field from './Field'
import Modal from './Modal'
import ModalForm from './ModalForm'
import Spinner from './Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from './Table'
import TextField from './TextField'

import { Store } from '../../types'

type ShippingGroupSettingsProps = {
  store: Store
}
type ShippingGroup = {
  name: string
  amount: number
  products: number
  id: number
  storeId: string
}
type ShippingGroupTableProps = {
  showDefaultModal: () => void
  edit: (groups: ShippingGroup[], groupId: number) => void
  remove: (groups: ShippingGroup[], groupId: number) => void
  title: string
  storeId: string
  groups: ShippingGroup[]
}
type ShippingGroupType = 'weight' | 'dimension' | 'ltl'
type ShippingGroupForm = {
  amount: number
  name: string
  shippingGroupTypeName: ShippingGroupType
  storeId: string
}
type DefaultModalProps = {
  open: boolean
  onClose: () => void
}
type RemoveModalProps = {
  storeId: string
  selected: ShippingGroup | null
  open: boolean
  onClose: () => void
  refetch: () => Promise<ApolloQueryResult<ShippingGroup>[]>
}
type EditModalProps = {
  storeId: string
  selected: ShippingGroup | null
  open: boolean
  onClose: () => void
  refetch: () => Promise<ApolloQueryResult<ShippingGroup>[]>
}
type ModalProps = {
  open: boolean
  storeId: string
  onClose: () => void
  refetch: () => Promise<ApolloQueryResult<ShippingGroup>[]>
}
type ShippingGroupEditForm = {
  id: number
  amount: number
  name: string
  storeId: string
}
const ShippingGroupEditSchema = yup.object({
  amount: yup.number().min(0).required().label('Shipping Cost'),
  name: yup.string().required().label('Name'),
})
const ShippingGroupSchema = yup.object({
  amount: yup.number().min(0).required().label(''),
  name: yup.string().required().label('Name'),
  shippingGroupTypeName: yup
    .string()
    .oneOf(['weight', 'dimension', 'ltl'])
    .required()
    .label('Shipping Group Type'),
})

const format = (value) => Money.format(Money.fromDecimal(value / 100, 'USD'))

const fromPayload = (payload): ShippingGroup => ({
  ...payload,
})

const selectedFromPayload = (payload): ShippingGroup => ({
  ...payload,
  amount: payload.amount / 100,
})

const maybeDefaultGroupStore = (groups) => {
  if (groups.length > 0) {
    return groups[0] && groups[0].storeId
  }
  return null
}

const ShippingGroupTable = ({
  storeId,
  showDefaultModal,
  edit,
  remove,
  title,
  groups,
}: ShippingGroupTableProps) => {
  const defaultGroupStoreId = maybeDefaultGroupStore(groups)

  return (
    <Card.Section title={title}>
      <Table>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Shipping Cost</Th>
            <Th>Products</Th>
            {defaultGroupStoreId && defaultGroupStoreId === storeId && <Th>Edit</Th>}
            {defaultGroupStoreId && defaultGroupStoreId === storeId && <Th>Remove</Th>}
            {defaultGroupStoreId && defaultGroupStoreId !== storeId && <Th>Default</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {groups.map((group) => (
            <Tr key={group.id}>
              <Td>{group.name}</Td>
              <Td>{format(group.amount)}</Td>
              <Td>{group.products}</Td>
              {group.storeId === storeId && (
                <Td>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      edit(groups, group.id)
                    }}
                    className="-mr-2 h-8 w-8 bg-white rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    <span className="sr-only">Edit Shipping Group</span>
                  </button>
                </Td>
              )}
              <Td>
                {group.storeId === storeId && (
                  <XCircleIcon
                    className="h-6 w-6 cursor-pointer text-red-400 hover:text-red-600"
                    onClick={(e) => {
                      e.preventDefault()
                      remove(groups, group.id)
                    }}
                  />
                )}
                {group.storeId !== storeId && (
                  <button
                    type="button"
                    onClick={showDefaultModal}
                    className="h-8 w-8 bg-white rounded-full flex items-center justify-center text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <svg
                      className="flex-shrink-0 h-5 w-5 text-gray-300"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Card.Section>
  )
}

const AddShippingGroupModal = ({ open, onClose, storeId, refetch }: ModalProps) => {
  const [spinnerAdd, spinner] = useToggle()
  const [createShippingGroupMutation] = useMutation(CREATE_SHIPPING_GROUP, {
    refetchQueries: [FETCH_SHIPPING_GROUPS],
  })
  // No `shouldUnregister`: no input registers `storeId`, and react-hook-form 7.89 then leaves it
  // out of the submitted values, so the create failed (GEA-11423).
  const form = useForm<ShippingGroupForm>({
    defaultValues: { storeId },
    resolver: yupResolver(ShippingGroupSchema),
  })

  // The modal stays mounted, so clear the form on close: the next open starts from the defaults.
  const close = () => {
    form.reset()
    onClose()
  }

  const createShippingGroup = async (newShippingGroup) => {
    spinner.on()

    const moneyAmount = Money.fromDecimal(newShippingGroup.amount, 'USD')
    const amount = moneyAmount?.amount
    const newShippingGroupPayload = {
      ...newShippingGroup,
      amount,
    }

    await createShippingGroupMutation({ variables: newShippingGroupPayload })

    await refetch()

    spinner.off()
    close()
  }

  return (
    <ModalForm
      open={open}
      onClose={close}
      title="New Shipping Group"
      onSubmit={form.handleSubmit(createShippingGroup)}
    >
      {spinnerAdd ? (
        <Spinner />
      ) : (
        <>
          <TextField
            label="Name"
            errors={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <TextField
            label="Shipping Cost"
            errors={form.formState.errors.amount && 'Shipping Cost must be a valid dollar amount'}
            type="number"
            step="any"
            min={0}
            {...form.register('amount')}
          />
          <Field
            label="Shipping Group Type"
            errors={form.formState.errors.shippingGroupTypeName?.message}
          >
            <select
              defaultValue="default"
              {...form.register('shippingGroupTypeName')}
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option disabled value="default">
                -- Select --
              </option>
              <option value="weight">Weight</option>
              <option value="dimension">Dimension</option>
              <option value="ltl">LTL</option>
            </select>
          </Field>
        </>
      )}
    </ModalForm>
  )
}

const DefaultMessagingModal = ({ open, onClose }: DefaultModalProps) => (
  <Modal open={open} onClose={onClose} title="Gearflow Shipping Group">
    <p className="text-sm text-gray-500">
      This is a default shipping group with predefined surcharges. To define a custom shipping group
      click &quot;add new&quot;
    </p>
  </Modal>
)

const EditShippingGroupModal = ({ storeId, selected, open, onClose, refetch }: EditModalProps) => {
  const [spinnerEdit, spinner] = useToggle()
  const selectedShippingGroup = selected || {}

  const form = useForm<ShippingGroupEditForm>({
    shouldUnregister: true,
    defaultValues: selectedShippingGroup,
    resolver: yupResolver(ShippingGroupEditSchema),
  })

  useEffect(() => {
    form.reset(selectedShippingGroup)
  }, [selected])

  const [editShippingGroupMutation] = useMutation(EDIT_SHIPPING_GROUP, {
    refetchQueries: [FETCH_SHIPPING_GROUPS],
  })

  const editShippingGroup = async (shippingGroup) => {
    spinner.on()

    const moneyAmount = Money.fromDecimal(shippingGroup.amount, 'USD')
    const amount = moneyAmount?.amount
    const variables = {
      ...shippingGroup,
      amount,
      groupId: selected && selected.id,
      storeId,
    }

    await editShippingGroupMutation({ variables })

    await refetch()

    spinner.off()
    onClose()
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="Edit Shipping Group"
      onSubmit={form.handleSubmit(editShippingGroup)}
    >
      {spinnerEdit ? (
        <Spinner />
      ) : (
        <>
          <TextField
            label="Name"
            errors={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <TextField
            label="Shipping Cost"
            errors={form.formState.errors.amount && 'Shipping Cost must be a valid dollar amount'}
            type="number"
            step="any"
            min={0}
            {...form.register('amount')}
          />
        </>
      )}
    </ModalForm>
  )
}

const RemoveShippingGroupModal = ({
  storeId,
  selected,
  open,
  onClose,
  refetch,
}: RemoveModalProps) => {
  const [spinnerRemove, spinner] = useToggle()
  const [removeShippingGroupMutation] = useMutation(REMOVE_SHIPPING_GROUP, {
    refetchQueries: [FETCH_SHIPPING_GROUPS],
  })

  const removeShippingGroup = async () => {
    spinner.on()

    const variables = {
      groupId: selected && selected.id,
      storeId,
    }

    await removeShippingGroupMutation({ variables })

    await refetch()

    spinner.off()
    onClose()
  }

  const text = `Removing this shipping group will unselect it from ${
    selected && selected.products
  } products`

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="Remove Shipping Group"
      onSubmit={(e) => {
        // The confirm has no fields. A form with a validation schema stopped the remove: under
        // react-hook-form 7.89 its values are empty, so the schema failed with no message (GEA-11423).
        e.preventDefault()
        removeShippingGroup()
      }}
    >
      {spinnerRemove ? (
        <Spinner />
      ) : (
        <>
          <p className="text-sm text-gray-500">{text}</p>
        </>
      )}
    </ModalForm>
  )
}

const ShippingGroupSettings = ({ store }: ShippingGroupSettingsProps) => {
  const [selected, setSelected] = useState<ShippingGroup | null>(null)
  const [showAddModal, addModalActions] = useToggle()
  const [showEditModal, editModalActions] = useToggle()
  const [showRemoveModal, removeModalActions] = useToggle()
  const [showDefaultModal, defaultModalActions] = useToggle()
  const weightVariables = {
    storeId: store.id,
    groupType: 'weight',
  }
  const dimensionVariables = {
    storeId: store.id,
    groupType: 'dimension',
  }
  const ltlVariables = {
    storeId: store.id,
    groupType: 'ltl',
  }
  const weightQuery = useQuery(FETCH_SHIPPING_GROUPS, {
    variables: weightVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
  const weightGroups = weightQuery?.data?.shippingGroups.map(fromPayload)

  const dimensionQuery = useQuery(FETCH_SHIPPING_GROUPS, {
    variables: dimensionVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
  const dimensionGroups = dimensionQuery?.data?.shippingGroups.map(fromPayload)

  const ltlQuery = useQuery(FETCH_SHIPPING_GROUPS, {
    variables: ltlVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
  const ltlGroups = ltlQuery?.data?.shippingGroups.map(fromPayload)

  const weightGroupRefetch = () => weightQuery?.refetch(weightVariables)
  const dimensionGroupRefetch = () => dimensionQuery?.refetch(dimensionVariables)
  const ltlGroupRefetch = () => ltlQuery?.refetch(ltlVariables)
  const refetchGroups = (): Promise<ApolloQueryResult<ShippingGroup>[]> =>
    Promise.all([weightGroupRefetch(), dimensionGroupRefetch(), ltlGroupRefetch()])

  const selectShippingGroup = (groups, groupId) => {
    const group = groups.find((g) => g.id === groupId)
    const selectedGroup = selectedFromPayload(group)
    setSelected(selectedGroup)
    editModalActions.on()
  }

  const removeShippingGroup = (groups, groupId) => {
    const group = groups.find((g) => g.id === groupId)
    const selectedGroup = selectedFromPayload(group)
    setSelected(selectedGroup)
    removeModalActions.on()
  }

  return (
    <Card
      title="Shipping Groups"
      headerButton={<Button title="Add New" onClick={addModalActions.on} />}
    >
      <DefaultMessagingModal open={showDefaultModal} onClose={defaultModalActions.off} />
      <RemoveShippingGroupModal
        storeId={store.id}
        selected={selected}
        open={showRemoveModal}
        onClose={removeModalActions.off}
        refetch={refetchGroups}
      />
      <EditShippingGroupModal
        storeId={store.id}
        selected={selected}
        open={showEditModal}
        onClose={editModalActions.off}
        refetch={refetchGroups}
      />
      <AddShippingGroupModal
        open={showAddModal}
        onClose={addModalActions.off}
        storeId={store.id}
        refetch={refetchGroups}
      />
      <Card.Section>
        <p className="text-sm text-gray-500">
          Create groups to account for shipping surcharges caused by oversized shipments.
        </p>
      </Card.Section>
      {weightQuery.loading || weightGroups.networkStatus === NetworkStatus.refetch ? (
        <Spinner />
      ) : (
        <ShippingGroupTable
          title="By Weight"
          storeId={store.id}
          showDefaultModal={defaultModalActions.on}
          groups={weightGroups}
          remove={removeShippingGroup}
          edit={selectShippingGroup}
        />
      )}
      {dimensionQuery.loading || dimensionGroups.networkStatus === NetworkStatus.refetch ? (
        <Spinner />
      ) : (
        <ShippingGroupTable
          title="By Dimension"
          storeId={store.id}
          showDefaultModal={defaultModalActions.on}
          groups={dimensionGroups}
          remove={removeShippingGroup}
          edit={selectShippingGroup}
        />
      )}
      {ltlQuery.loading || ltlGroups.networkStatus === NetworkStatus.refetch ? (
        <Spinner />
      ) : (
        <ShippingGroupTable
          title="Ltl"
          storeId={store.id}
          showDefaultModal={defaultModalActions.on}
          groups={ltlGroups}
          remove={removeShippingGroup}
          edit={selectShippingGroup}
        />
      )}
    </Card>
  )
}

export default ShippingGroupSettings
